/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, signal,
} from '@angular/core';
import { ArticleService } from '../services/article.service';
import { FeedStoreService } from '../services/feed-store.service';
import { focusNewItem } from '../shared/focus-new-item';

type ArticlesTab = 'official' | 'community' | 'popular';

const ARTICLES_TABS: { value: ArticlesTab; label: string }[] = [
  { value: 'official', label: 'Official' },
  { value: 'community', label: 'Community' },
  { value: 'popular', label: 'Most popular' },
];

const DEFAULT_HEADER = {
  title: 'Software Testing Articles',
  description: 'Check out articles from professional testers -- on all topics related to software testing, QA, career advice, devices and more -- and write your own.',
  coverBg: {
    small: '/utest-static/assets/images/hero/articles-bg-xs.jpg',
    large: '/utest-static/assets/images/hero/articles-bg.jpg',
  },
};

// /articles in the 2026 design: a cover header, then one tabbed grid instead of three stacked sections. Same three feeds as ut-articles (6 per page), but each tab loads the first time it's opened.
@Component({
  selector: 'ut-articles-page',
  templateUrl: './articles-page.component.html',
  styleUrls: ['./articles-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArticlesPageComponent implements OnInit {
  // The root state's `user`: null when logged out.
  @Input() user: any;

  readonly tabs = ARTICLES_TABS;

  tab = signal<ArticlesTab>('official');
  // The live region's text. Set (not just derived) on every tab switch, with the tab's name, so switching to a tab
  // with the same article count as the last one still announces something.
  announcement = signal('');
  header = signal(DEFAULT_HEADER);
  stores: Record<ArticlesTab, any>;

  constructor(
    private articleService: ArticleService,
    private feedStore: FeedStoreService,
    @Inject('StaticValuesService') private StaticValuesService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.StaticValuesService.pageData('articles').then((res) => this.header.set({ ...DEFAULT_HEADER, ...res.data, coverBg: { ...DEFAULT_HEADER.coverBg, ...res.data?.coverBg } }), () => {});
    const fetch = (params) => this.articleService.feed(params);
    const yearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString();
    this.stores = {
      official: this.feedStore.create(fetch, { per_page: 6, filter: { article_type: 'official' } }),
      community: this.feedStore.create(fetch, { per_page: 6, filter: { article_type: 'community' } }),
      popular: this.feedStore.create(fetch, { per_page: 6, sort: '-popularity', filter: { published_after: yearAgo } }),
    };
    this.selectTab('official');
  }

  get store(): any {
    return this.stores[this.tab()];
  }

  get cards(): any[] {
    return this.store.feedItems;
  }

  cover(src: string): string {
    return `url('${src}')`;
  }

  get canCreate(): boolean {
    return !!this.user?.current_user_permissions?.create_article;
  }

  checkLightTester(event: Event): void {
    if (this.LightTesterErrorService.show()) event.preventDefault();
  }

  selectTab(tab: ArticlesTab): void {
    this.tab.set(tab);
    this.announce();
    this.track(this.store.loadOnce());
  }

  retry(): void {
    this.track(this.store.load());
  }

  get status(): string {
    if (this.store.isLoading) return 'Loading articles';
    if (this.store.hasFailed) return 'Articles couldn\'t load';
    return this.store.feedItems.length === 1 ? '1 article shown' : `${this.store.feedItems.length} articles shown`;
  }

  loadMore(): void {
    const { store } = this;
    const next = store.feedItems.length;
    this.cdr.markForCheck();
    store.loadMore().then(() => {
      this.cdr.markForCheck();
      this.announce();
      const article = store.feedItems[next];
      if (article) focusNewItem(this.el.nativeElement, `#article_${article.id} .feed-item`);
    });
  }

  private announce(): void {
    this.announcement.set(`${this.tabs.find((t) => t.value === this.tab()).label}: ${this.status}`);
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): void {
    this.cdr.markForCheck();
    promise.then(() => {
      this.cdr.markForCheck();
      this.announce();
    });
  }
}
